import { Panel } from './Panel';
import { LoadContext, SoundEffect } from "../../../utils";
import { PING_SFX } from '../../../constants';
import { Settings } from "../../../settings";
import { Sprite } from 'pixi.js';
import { Focusable, InteractiveWrapper } from "../../../ui";
import { Game } from '../../../game';
import { Slider } from '../ui';

export class Volume extends Panel
{
    private musicSlider:Slider;
    private sfxSlider:Slider;
    private voiceSlider:Slider;
    private sfx: SoundEffect;

    public static preload() {
        const loader = LoadContext.current;
        loader.add('Settings', './assets/accessibility/Settings{size}.json');
        loader.add(Settings.SfxStyle.SFX_1, `./assets/sfx/${PING_SFX[Settings.SfxStyle.SFX_1]}`);
    }

    constructor( game:Game )
    {
        super(
            game,
            "Volume",
            {
                title: "CYB_Panel2_Volume",
                explain: 'CYB_Panel2_Volume_Explanation',
                settings: 'CYB_Panel2_Volume_Settings',
            },
            {width: 670, height: 501}
        );

        const loader = LoadContext.current;

        this.sfx = new SoundEffect(loader.getAsset(Settings.SfxStyle.SFX_1), game);

        const icon:Sprite = new Sprite( loader.getSpritesheet('Settings').textures['icon_volume'] );
        icon.position.set( this.icon_x, this.icon_y );
        this.addChild( icon );


        this.musicSlider = new Slider(game.app.state.musicVolume, 'Music', loader.getSpritesheet('Settings').textures['icon_music']);
        this.musicSlider.position.set( 375, 100 );
        this.addChild( this.musicSlider );
        this.disposables.add(new InteractiveWrapper(this.musicSlider, {
            onHover: () => {
                this.game.vo.playHighlight(`CYB_Panel2_Music`);
            },
            onFocus: () => {
                this.game.vo.playHighlight([`CYB_Panel2_Music`, 'CYB_Panel2_Controls']);
            },
            onTap: () => {}
        }));
        this.musicSlider.on('changed', () => {
            // don't play feedback if we are not on screen (as settings panel is kept around
            // until scene change
            if (!this.worldVisible) return;
            this.game.vo.stop();
            this.game.vo.playVO(`CYB_Panel2_Music${this.voFromValue(this.musicSlider.value)}`);
            this.game.logEvent("change_setting", "music_volume_" + this.musicSlider.value * 5);
        });


        this.sfxSlider = new Slider(game.app.state.sfxVolume, 'Sound Effects', loader.getSpritesheet('Settings').textures['icon_soundeffects']);
        this.sfxSlider.position.set( 375, 225 );
        this.addChild( this.sfxSlider );
        this.disposables.add(new InteractiveWrapper(this.sfxSlider, {
            onHover: () => {
                this.game.vo.playHighlight(`CYB_Panel2_SoundEffects`);
            },
            onFocus: () => {
                this.game.vo.playHighlight([`CYB_Panel2_SoundEffects`, 'CYB_Panel2_Controls']);
            },
            onTap: () => {}
        }));
        this.sfxSlider.on('changed', async () => {
            // don't play feedback if we are not on screen (as settings panel is kept around
            // until scene change
            if (!this.worldVisible) return;
            this.game.vo.stop();
            this.game.logEvent("change_setting", "sfx_volume_" + this.sfxSlider.value * 5);
            await this.game.vo.playVO(`CYB_Panel2_SoundEffects${this.voFromValue(this.sfxSlider.value)}`);
            this.sfx.play();
        });

        this.voiceSlider = new Slider(game.app.state.voVolume, 'Voice', loader.getSpritesheet('Settings').textures['icon_voice']);
        this.voiceSlider.position.set( 375, 350 );
        this.addChild( this.voiceSlider );
        this.disposables.add(new InteractiveWrapper(this.voiceSlider, {
            onHover: () => {
                this.game.vo.playHighlight(`CYB_Panel2_Voice`);
            },
            onFocus: () => {
                this.game.vo.playHighlight([`CYB_Panel2_Voice`, 'CYB_Panel2_Controls']);
            },
            onTap: () => {}
        }));
        this.voiceSlider.on('changed', () => {
            // don't play feedback if we are not on screen (as settings panel is kept around
            // until scene change
            if (!this.worldVisible) return;
            this.game.vo.stop();
            this.game.vo.playVO(`CYB_Panel2_Voice${this.voFromValue(this.voiceSlider.value)}`);
            this.game.logEvent("change_setting", "voice_volume_" + this.voiceSlider.value * 5);
        });

        this.updateToMatchSetting();

    }

    private voFromValue(value:number)
    {
        if (value === 0) return 'Off';
        if (value <= 0.2) return '1';
        if (value <= 0.4) return '2';
        if (value <= 0.6) return '3';
        if (value <= 0.8) return '4';
        return '5';
    }

    getFocusItems():Focusable[]
    {
        let items = super.getFocusItems();
        return items.concat( [
            this.musicSlider,
            this.sfxSlider,
            this.voiceSlider
        ]);
    }

    destroy()
    {
        this.sfx.destroy();

        super.destroy();
    }
}